/**
 * useAlertEngine.js — Multi-tier, multi-symbol alert engine manager
 *
 * Architecture:
 *   - One AlertEngine instance per watched symbol (engines map keyed by symbol)
 *   - Tier settings are shared across all symbols (symbol-agnostic)
 *   - Each engine has independent indicator state (bars, persist counters)
 *   - Global settings: Pushover credentials, watched symbols list
 *   - Existing tier KV keys are unchanged — fully backward compatible
 *
 * Watched symbols are stored in globalSettings.watchedSymbols (array of uppercase strings).
 * Default: ['SPY']. Adding 'NVDA' spins up a second engine automatically.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertEngine } from '../lib/alertEngine';
import { TIER_IDS, TIER_DEFAULTS, TIER_META } from '../lib/alertTiers';
import { isMarketHours } from '../lib/marketHours';
import { classifySessionRegime } from '../lib/sessionRegime';
import {
  alertGetSettings, alertPutSettings,
  alertGetLog, alertAppendLog, alertClearLog,
  alertSendPushover,
  tierGetSettings, tierPutSettings,
  tierGetRingtone,
  regimeDailyPut,
} from '../lib/api';

export const DEFAULT_SAFE_DAY_ALERT = {
  enabled:      true,
  audioEnabled: true,
  volume:       0.7,
  browserNotif: false,
  cooldownMins: 5,
  ringtoneCutoffSecs: 0,
  gradualVolume: false,
  alertTypes: {
    STOP_LOSS:   { enabled: true,  label: '🛑 Stop Loss',    color: '#ef4444', severity: 'bearish' },
    DANGER:      { enabled: true,  label: '⚠ Danger Zone',  color: '#f97316', severity: 'bearish' },
    EXIT_SIGNAL: { enabled: true,  label: '↩ Exit Signal',  color: '#f59e0b', severity: 'bearish' },
    TAKE_PROFIT: { enabled: true,  label: '✓ Take Profit',  color: '#22c55e', severity: 'bullish' },
    CAUTION:     { enabled: false, label: '〰 Caution',      color: '#f59e0b', severity: 'bearish' },
  },
  indicators: {
    macd: {
      enabled: true, timeframeMins: 1,
      fast: 12, slow: 26, signal: 9,
      // On 1-min bars: available after 34 min (9:30 → ~10:04 ET)
    },
    maCross: {
      enabled: true, timeframeMins: 5,
      fastPeriod: 9, slowPeriod: 20,
      // On 5-min bars: available after 100 min (~11:10 ET)
    },
    stochastic: {
      enabled: true, timeframeMins: 1,
      kPeriod: 14, dPeriod: 3,
      overbought: 80, oversold: 20,
      // On 1-min bars: available after ~17 min (very early)
    },
    rsiExit: {
      enabled: true, timeframeMins: 1,
      period: 14, neutralZone: 50,
      // Bull put: exit when RSI < neutralZone. Bear call: exit when RSI > (100-neutralZone).
    },
    vwapCross: {
      enabled: true,
      bandPct: 0.05, // must cross by this % to count (avoids noise at VWAP)
    },
    atrTrail: {
      enabled: false,
      multiplier: 2.0,
      // Alerts when price moves multiplier×ATR against the short strike
    },
    bbWidth: {
      enabled: false, timeframeMins: 5,
      squeezeThreshold: 0.10, // BB width % below which a squeeze is flagged
    },
  },
};

export const DEFAULT_GAME_DAY_ALERT = {
  ticker:      'NVDA',
  enabled:     true,
  audioEnabled: true,
  volume:      0.7,
  browserNotif: false,
  cooldownMins: 3,
  ringtoneCutoffSecs: 0,
  gradualVolume: false,
  alertTypes: {
    STOP_LOSS:   { enabled: true,  label: '🛑 Stop Loss',    color: '#ef4444', severity: 'bearish' },
    TAKE_PROFIT: { enabled: true,  label: '✓ Take Profit',  color: '#22c55e', severity: 'bullish' },
    EXIT_SIGNAL: { enabled: true,  label: '↩ Exit Signal',  color: '#f59e0b', severity: 'bearish' },
    MOMENTUM:    { enabled: true,  label: '⚡ Momentum',     color: '#818cf8', severity: 'bullish' },
    CAUTION:     { enabled: false, label: '〰 Caution',      color: '#f59e0b', severity: 'bearish' },
  },
  indicators: {
    macd:       { enabled:true, timeframeMins:1, fast:12, slow:26, signal:9 },
    maCross:    { enabled:true, timeframeMins:5, fastPeriod:9, slowPeriod:20 },
    stochastic: { enabled:true, timeframeMins:1, kPeriod:14, dPeriod:3, overbought:80, oversold:20 },
    rsiExit:    { enabled:true, timeframeMins:1, period:14, neutralZone:50 },
    vwapCross:  { enabled:true, bandPct:0.05 },
  },
};

export const DEFAULT_GLOBAL = {
  pushoverToken:   '',
  pushoverUser:    '',
  browserNotif:    false,
  watchedSymbols:  ['SPY'],
  // Session Guard
  warningTime:     '',
  warningMessage:  'You have reached your scheduled review time. Assess your session before continuing.',
  maxDailyLoss:    0,
  // Safe Day instrument preference
  spyMode:         'SPY',
  // Tab alert configs
  safeDayAlerts:   DEFAULT_SAFE_DAY_ALERT,
  gameDayAlerts:   DEFAULT_GAME_DAY_ALERT,
};

// ── Audio ─────────────────────────────────────────────────────────────────────

export function playBeep(severity='bullish',volume=0.7){
  try{
    const ctx=new(window.AudioContext||window.webkitAudioContext)();
    const osc=ctx.createOscillator(),gain=ctx.createGain();
    osc.connect(gain);gain.connect(ctx.destination);osc.type='sine';
    osc.frequency.setValueAtTime(severity==='bullish'?880:440,ctx.currentTime);
    gain.gain.setValueAtTime(volume,ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.45);
    osc.start();osc.stop(ctx.currentTime+0.45);
  }catch{}
}

export function playAudio(audioEl,{targetVol=0.7,cutoffSecs=0,gradual=false}){
  if(!audioEl)return;
  audioEl.currentTime=0;
  if(gradual&&cutoffSecs>0){
    try{
      const ctx=new(window.AudioContext||window.webkitAudioContext)();
      const src=ctx.createMediaElementSource(audioEl),gain=ctx.createGain();
      src.connect(gain);gain.connect(ctx.destination);
      const sv=Math.max(0.05,targetVol*0.3);
      gain.gain.setValueAtTime(sv,ctx.currentTime);
      gain.gain.linearRampToValueAtTime(Math.min(1,targetVol*1.3),ctx.currentTime+cutoffSecs);
      audioEl.volume=1;audioEl.play().catch(()=>{});
      setTimeout(()=>{try{audioEl.pause();audioEl.currentTime=0;ctx.close();}catch{}},cutoffSecs*1000);
      return;
    }catch{}
  }
  audioEl.volume=Math.min(1,targetVol);
  audioEl.play().catch(()=>{});
  if(cutoffSecs>0)setTimeout(()=>{try{audioEl.pause();audioEl.currentTime=0;}catch{}},cutoffSecs*1000);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAlertEngine(){
  const [globalSettings, setGlobalSettings] = useState(DEFAULT_GLOBAL);
  const [tiers, setTiers] = useState(() =>
    Object.fromEntries(TIER_IDS.map(id=>[id,{settings:{...TIER_DEFAULTS[id]},ringtoneUrl:null,ringtoneName:''}]))
  );

  // Per-symbol live state: { [symbol]: state | null }
  const [symbolStates, setSymbolStates] = useState({});

  const [engineOn,         setEngineOn]         = useState(false);
  const [muted,            setMuted]             = useState(false);
  const [alertCount,       setAlertCount]        = useState(0);
  const [tierAlertCounts,  setTierAlertCounts]   = useState({tier1:0,tier2:0,tier3:0});
  const [symbolAlertCounts,setSymbolAlertCounts] = useState({});
  const [log,              setLog]               = useState([]);

  // Refs
  const enginesRef      = useRef({});   // symbol → AlertEngine
  const tiersRef        = useRef(tiers);
  const globalRef       = useRef(globalSettings);
  const mutedRef        = useRef(false);
  const audioRefs       = useRef({});   // tierId → Audio element
  const marketWasOpen   = useRef(false);
  const engineOnRef     = useRef(false);

  useEffect(()=>{tiersRef.current=tiers;},[tiers]);
  useEffect(()=>{globalRef.current=globalSettings;},[globalSettings]);
  useEffect(()=>{mutedRef.current=muted;},[muted]);
  useEffect(()=>{engineOnRef.current=engineOn;},[engineOn]);

  // ── Sync engines to watchedSymbols ────────────────────────────────────────
  // Called whenever globalSettings.watchedSymbols changes or on start/stop
  const syncEngines = useCallback((symbols, shouldRun) => {
    const current = enginesRef.current;

    // Stop + remove engines for symbols no longer watched
    for (const sym of Object.keys(current)) {
      if (!symbols.includes(sym)) {
        current[sym].stop();
        delete current[sym];
        setSymbolStates(prev => { const n={...prev}; delete n[sym]; return n; });
        setSymbolAlertCounts(prev => { const n={...prev}; delete n[sym]; return n; });
      }
    }

    // Add engines for new symbols
    for (const sym of symbols) {
      if (!current[sym]) {
        const engine = new AlertEngine({ symbol: sym, onAlert: handleEngineEvent });
        // Push current tier settings
        for (const id of TIER_IDS) engine.updateTierSettings(id, tiersRef.current[id].settings);
        current[sym] = engine;
        if (shouldRun) engine.start();
      }
    }
  }, []);

  // ── Load all data on login ────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    // Global settings first (need watchedSymbols before syncing engines)
    let loadedGlobal = DEFAULT_GLOBAL;
    try {
      const g = await alertGetSettings();
      if (g) loadedGlobal = { ...DEFAULT_GLOBAL, ...g };
    } catch {}
    setGlobalSettings(loadedGlobal);
    globalRef.current = loadedGlobal;

    // Per-tier settings + ringtones in parallel
    const tierUpdates = {};
    await Promise.all(TIER_IDS.map(async (id) => {
      const u = { settings: { ...TIER_DEFAULTS[id] }, ringtoneUrl: null, ringtoneName: '' };
      try { const s = await tierGetSettings(id); if (s) u.settings = { ...TIER_DEFAULTS[id], ...s }; } catch {}
      try { const url = await tierGetRingtone(id); if (url) { u.ringtoneUrl = url; u.ringtoneName = 'Saved tone'; } } catch {}
      tierUpdates[id] = u;
    }));

    setTiers(prev => {
      const next = { ...prev };
      for (const id of TIER_IDS) next[id] = { ...prev[id], ...tierUpdates[id] };
      return next;
    });
    tiersRef.current = { ...tiersRef.current, ...tierUpdates };

    // Push loaded settings into all existing engines
    for (const engine of Object.values(enginesRef.current)) {
      for (const id of TIER_IDS) engine.updateTierSettings(id, tierUpdates[id].settings);
    }

    // Sync engines to watchedSymbols
    const symbols = loadedGlobal.watchedSymbols?.length ? loadedGlobal.watchedSymbols : ['SPY'];
    syncEngines(symbols, engineOnRef.current);

    // Log
    try { const data = await alertGetLog(); setLog(data || []); } catch {}
  }, [syncEngines]);

  // ── Engine event handler ──────────────────────────────────────────────────
  const handleEngineEvent = useCallback(async (event) => {
    if (event.type === 'state') {
      // event.state is null on fetch error or market closed; set symbol state accordingly
      setSymbolStates(prev => ({ ...prev, [event.symbol]: event.state ?? null }));
      return;
    }
    if (event.type !== 'signal') return;

    const { tierId, symbol } = event;
    const tierState = tiersRef.current[tierId];
    const s         = tierState?.settings ?? {};
    const global    = globalRef.current;

    // 1. Audio
    if (s.audioEnabled !== false && !mutedRef.current) {
      const url = tierState?.ringtoneUrl;
      if (url) {
        if (!audioRefs.current[tierId] || audioRefs.current[tierId]._src !== url) {
          audioRefs.current[tierId] = new Audio(url);
          audioRefs.current[tierId]._src = url;
        }
        playAudio(audioRefs.current[tierId], {
          targetVol:  s.audioVolume       ?? 0.7,
          cutoffSecs: s.ringtoneCutoffSecs ?? 0,
          gradual:    s.gradualVolume      ?? false,
        });
      } else {
        playBeep(event.severity, s.audioVolume ?? 0.7);
      }
    }

    // 2. Browser notification
    if ((s.browserNotif || global.browserNotif) && Notification?.permission === 'granted') {
      const meta = TIER_META[tierId];
      new Notification(`${symbol} — ${event.label} [${meta.name}]`, {
        body: event.detail, icon: '/favicon.ico', tag: `${tierId}:${symbol}:${event.key}`,
      });
    }

    // 3. Pushover
    if (s.pushoverEnabled !== false) {
      const token   = s.pushoverTokenOverride || global.pushoverToken;
      const userKey = s.pushoverUserOverride  || global.pushoverUser;
      if (token && userKey) {
        const meta = TIER_META[tierId];
        alertSendPushover(token, userKey,
          `[${meta.name}] ${symbol} — ${event.label}`, event.detail,
          s.pushoverPriority ?? 1
        ).catch(()=>{});
      }
    }

    // 4. Log
    const entry = {
      id: crypto.randomUUID(), type: 'signal',
      symbol, tierId, key: event.key, label: event.label, severity: event.severity,
      detail: event.detail, price: event.price, rsi: event.rsi,
      confluenceCount: event.confluenceCount, activeSignals: event.activeSignals,
      timestamp: event.timestamp,
    };
    setLog(prev => [entry, ...prev].slice(0, 500));
    setAlertCount(c => c + 1);
    setTierAlertCounts(prev => ({ ...prev, [tierId]: (prev[tierId] || 0) + 1 }));
    setSymbolAlertCounts(prev => ({ ...prev, [symbol]: (prev[symbol] || 0) + 1 }));
    alertAppendLog(entry).catch(() => {});
  }, []);

  // ── Auto-enable on market open ────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const open = isMarketHours();
      if (open && !marketWasOpen.current) {
        const t = tiersRef.current;
        const anyAuto = TIER_IDS.some(id => t[id]?.settings?.autoEnableOnOpen);
        if (anyAuto && !engineOnRef.current) {
          const symbols = globalRef.current.watchedSymbols?.length
            ? globalRef.current.watchedSymbols : ['SPY'];
          for (const engine of Object.values(enginesRef.current)) engine.start();
          setEngineOn(true);
          setAlertCount(0);
          setTierAlertCounts({ tier1: 0, tier2: 0, tier3: 0 });
          setSymbolAlertCounts({});
        }
        for (const engine of Object.values(enginesRef.current)) {
          for (const id of TIER_IDS) engine.resetTier(id);
        }
      }
      marketWasOpen.current = open;
    };
    check();
    const t = setInterval(check, 60_000);
    return () => clearInterval(t);
  }, []);

  // ── Public API ────────────────────────────────────────────────────────────

  const start = useCallback(() => {
    const symbols = globalRef.current.watchedSymbols?.length
      ? globalRef.current.watchedSymbols : ['SPY'];
    syncEngines(symbols, true);
    for (const engine of Object.values(enginesRef.current)) engine.start();
    setEngineOn(true);
    setAlertCount(0);
    setTierAlertCounts({ tier1: 0, tier2: 0, tier3: 0 });
    setSymbolAlertCounts({});
    localStorage.setItem('od:engineWasOn', '1');
  }, [syncEngines]);

  const stop = useCallback(() => {
    for (const engine of Object.values(enginesRef.current)) engine.stop();
    setEngineOn(false);
    setSymbolStates({});
    setAlertCount(0);
    localStorage.removeItem('od:engineWasOn');
  }, []);

  // Auto-restart engine after page refresh if it was running before
  useEffect(() => {
    if (localStorage.getItem('od:engineWasOn') === '1') {
      const symbols = globalRef.current.watchedSymbols?.length
        ? globalRef.current.watchedSymbols : ['SPY'];
      syncEngines(symbols, true);
      for (const engine of Object.values(enginesRef.current)) engine.start();
      setEngineOn(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mute   = useCallback(() => setMuted(true),  []);
  const unmute = useCallback(() => setMuted(false), []);

  const injectMarketData = useCallback((data) => {
    for (const engine of Object.values(enginesRef.current)) engine.injectMarketData(data);
  }, []);

  const saveGlobalSettings = useCallback(async (draft) => {
    await alertPutSettings(draft);
    const merged = { ...DEFAULT_GLOBAL, ...draft };
    setGlobalSettings(merged);
    globalRef.current = merged;
    // Sync engines if symbol list changed
    const symbols = merged.watchedSymbols?.length ? merged.watchedSymbols : ['SPY'];
    syncEngines(symbols, engineOnRef.current);
  }, [syncEngines]);

  const saveTierSettings = useCallback(async (tierId, draft) => {
    await tierPutSettings(tierId, draft);
    const merged = { ...TIER_DEFAULTS[tierId], ...draft };
    setTiers(prev => ({ ...prev, [tierId]: { ...prev[tierId], settings: merged } }));
    tiersRef.current[tierId] = { ...tiersRef.current[tierId], settings: merged };
    // Push to all symbol engines
    for (const engine of Object.values(enginesRef.current)) engine.updateTierSettings(tierId, merged);
  }, []);

  const setTierRingtone = useCallback((tierId, url, name = '') => {
    setTiers(prev => ({ ...prev, [tierId]: { ...prev[tierId], ringtoneUrl: url, ringtoneName: name } }));
    delete audioRefs.current[tierId];
  }, []);

  const clearLog       = useCallback(async () => { await alertClearLog(); setLog([]); }, []);
  const deleteLogEntry = useCallback((id) => setLog(p => p.filter(e => e.id !== id)), []);

  // Computed: combined live state (for backward compat with LiveMonitorPanel which shows one symbol)
  // Returns state for the first symbol (usually SPY)
  const primarySymbol   = globalRef.current.watchedSymbols?.[0] ?? 'SPY';
  const liveState       = symbolStates[primarySymbol] ?? null;

  // Save regime at market close
  const marketOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = isMarketHours();
    if (marketOpenRef.current && !isOpen && engineOn) {
      const spySt = symbolStates['SPY'] ?? liveState;
      if (spySt?.bars?.length >= 10) {
        const v = classifySessionRegime(spySt.bars, spySt.adx, spySt.vwap);
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(new Date());
        const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
        const today = `${p.year}-${p.month}-${p.day}`;
        regimeDailyPut(today, {
          regime: v.regime, label: v.label, confidence: v.confidence,
          color: v.color, trendScore: v.trendScore,
          adx: v.metrics?.adx, netMovePct: v.metrics?.netMovePct,
        }).catch(() => {});
      }
    }
    marketOpenRef.current = isOpen;
  });

  return {
    engineOn, muted, liveState, symbolStates, alertCount,
    tierAlertCounts, symbolAlertCounts,
    tiers, globalSettings,
    log,
    start, stop, mute, unmute,
    loadAll, saveGlobalSettings, saveTierSettings, setTierRingtone,
    injectMarketData, clearLog, deleteLogEntry,
  };
}
